import { useEffect, useCallback, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { firebaseUserAtom } from '@/stores/auth';
import { RevisionLog } from '@/types';
import { queryKeys } from '@/lib/queryKeys';
import {
  subscribeToRevisionLogs,
  addRevisionLog,
  clearRevisionLogs,
  AddRevisionInput,
} from '@/repositories/revision-log.repository';
import { calculateRevisionSummary, RevisionSummary } from '@/services/revision.service';

// Re-export types for consumers
export type { AddRevisionInput } from '@/repositories/revision-log.repository';
export type { RevisionSummary } from '@/services/revision.service';

export interface UseRevisionLogResult {
  revisions: RevisionLog[];
  isLoading: boolean;
  addRevision: (data: AddRevisionInput) => Promise<boolean>;
  clearRevisions: () => Promise<boolean>;
  getRevisionSummary: () => RevisionSummary;
}

export function useRevisionLog(date: string): UseRevisionLogResult {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const queryClient = useQueryClient();
  const [isListenerInitialized, setIsListenerInitialized] = useState(false);
  const prevKeyRef = useRef<string | null>(null);

  const userId = firebaseUser?.uid;
  const queryKey = queryKeys.revisionLog.byDate(userId, date);
  const cacheKey = `${userId}-${date}-revisions`;

  // Use React Query for caching and state management
  const { data: revisions = [] } = useQuery<RevisionLog[], Error>({
    queryKey,
    queryFn: async () => [],
    enabled: !!userId,
    staleTime: Infinity,
  });

  // Set up real-time Firestore listener
  useEffect(() => {
    if (prevKeyRef.current !== cacheKey) {
      prevKeyRef.current = cacheKey;
      setIsListenerInitialized(false);
    }

    if (!userId) {
      setIsListenerInitialized(true);
      return;
    }

    let isCleanedUp = false;

    const timeoutId = setTimeout(() => {
      if (!isCleanedUp && !isListenerInitialized) {
        console.warn('Revision log listener timeout - marking as initialized');
        setIsListenerInitialized(true);
      }
    }, 2000);

    const unsubscribe = subscribeToRevisionLogs(
      userId,
      date,
      (sortedRevisions) => {
        if (isCleanedUp) return;
        queryClient.setQueryData(queryKey, sortedRevisions);
        setIsListenerInitialized(true);
      },
      () => {
        if (!isCleanedUp) {
          setIsListenerInitialized(true);
        }
      }
    );

    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Add a new revision
  const addRevision = useCallback(
    async (data: AddRevisionInput): Promise<boolean> => {
      if (!userId) {
        console.error('User not authenticated');
        return false;
      }

      try {
        await addRevisionLog(userId, date, data);
        return true;
      } catch (error) {
        console.error('Error adding revision:', error);
        return false;
      }
    },
    [userId, date]
  );

  // Clear all revisions for the day
  const clearRevisions = useCallback(async (): Promise<boolean> => {
    if (!userId) {
      console.error('User not authenticated');
      return false;
    }

    try {
      await clearRevisionLogs(userId, date);
      return true;
    } catch (error) {
      console.error('Error clearing revisions:', error);
      return false;
    }
  }, [userId, date]);

  // Get revision summary for the day
  const getRevisionSummary = useCallback((): RevisionSummary => {
    return calculateRevisionSummary(revisions);
  }, [revisions]);

  // Compute actual loading state
  const hasRevisions = revisions.length > 0;
  const isLoading = !!userId && !isListenerInitialized && !hasRevisions;

  return {
    revisions,
    isLoading,
    addRevision,
    clearRevisions,
    getRevisionSummary,
  };
}

// ============================================
// Standalone function for use in useBlocks
// ============================================

/**
 * Add a revision log entry directly to Firestore.
 * This is used by useBlocks to log changes without needing the full hook.
 */
export async function logBlockRevision(
  userId: string,
  date: string,
  data: AddRevisionInput
): Promise<boolean> {
  if (!userId) {
    console.error('User not authenticated');
    return false;
  }

  try {
    await addRevisionLog(userId, date, data);
    return true;
  } catch (error) {
    console.error('Error logging block revision:', error);
    return false;
  }
}
